import { supabase } from '../services/supabase';
import { misLecturas, TOPE_IDS } from './lecturas';

/**
 * Los roles que forman el buzón. Lista cerrada y a propósito: el cuerpo técnico
 * NO pinta nada aquí —un monitor tiene su propia pantalla— y en VERTEX no hay
 * ningún rol de coordinación. Un test lo fija para que ampliarlo tenga que ser
 * una decisión, no un descuido.
 */
export const ROLES_DEL_BUZON = ['admin', 'director'] as const;

/**
 * Los roles que forman LA OTRA MITAD: quien le escribe al club.
 *
 * Esta lista estaba escrita a mano dentro de `ComunicacionesPage` —la consulta
 * «la lista de personas»— y de ella salen las CONVERSACIONES que se pintan. Se
 * sube aquí porque desde la mig 324 también decide **lo que cuenta el globo**:
 * eran dos listas iguales en dos sitios, y el día que una creciera el círculo y
 * la lista dejarían de decir lo mismo sin que nada se pusiera en rojo.
 *
 * admin y director quedan FUERA a propósito: el club no es una familia. Un
 * mensaje del club al club no es correo entrante, y es justo el que sobraba.
 */
export const ROLES_DE_FAMILIA = ['alumno', 'monitor', 'tutor'] as const;

/**
 * ¿Es este mensaje CORREO ENTRANTE del club, escrito por alguien de `familia`?
 *
 * EL PREDICADO, Y VIVE EN UN SOLO SITIO. Antes había dos:
 *
 *   · el círculo de la barra lateral contaba por DESTINATARIO —todo lo que
 *     recibe el buzón, sin mirar quién escribe—;
 *   · la lista pintaba por REMITENTE —solo lo que escribe una familia—.
 *
 * Medido en producción el 10-10-2026 con la cuenta `Admin VERTEX`, que es la que
 * usa dirección: **el círculo decía 4 y la lista sumaba 0**, sin un solo mensaje
 * en común. Los 4 eran mensajes que la propia cuenta admin escribió el 29-sep al
 * buzón de un director: correo SALIENTE, contado como entrante. De los 21
 * mensajes del buzón, 17 caen en alguna conversación y esos 4 en ninguna, así
 * que no había dónde abrirlos para marcarlos leídos: el número no podía bajar
 * nunca. «Siempre 4.»
 *
 * Y no se pierde nada al dejar de contarlos: el chat de admin de la app SÍ pinta
 * ese hilo (agrupa por «el que no es el buzón», sin filtro de rol), así que ese
 * correo se puede leer. Medido también.
 *
 * `familia` es un conjunto de ids: la barra lateral le pasa TODAS las personas
 * que pueden escribir al club, y la lista le pasa los miembros de UNA casa para
 * el globo de esa fila. Es la misma pregunta a dos escalas, y por eso es la
 * misma función.
 */
export function esCorreoEntranteDelClub(
  m: { sender_id: string; receiver_id: string | null; is_broadcast?: boolean | null },
  buzon: ReadonlySet<string>,
  familia: ReadonlySet<string>
): boolean {
  if (m.is_broadcast) return false;
  if (!m.receiver_id || !buzon.has(m.receiver_id)) return false;
  return familia.has(m.sender_id);
}

/**
 * Los ids de quien puede escribirle al club (los `ROLES_DE_FAMILIA` activos).
 *
 * Devuelve `null` si no se ha podido leer, y el llamante NO cae a un conjunto
 * vacío: con `familia = {}` el predicado de arriba diría «nada es entrante» y el
 * globo se apagaría afirmando que no hay nada sin leer. Es el mismo 0 que no
 * distingue «no hay» de «no he podido mirar».
 */
export async function idsDeFamilia(): Promise<{ ids: string[] | null; error: string | null }> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id')
    .in('role', ROLES_DE_FAMILIA as unknown as string[])
    .eq('active', true);
  if (error) return { ids: null, error: error.message || 'Error desconocido' };
  return { ids: ((data ?? []) as { id: string }[]).map((f) => f.id), error: null };
}

/**
 * Los ids que forman el BUZÓN DEL CLUB, más el del propio usuario.
 *
 * Cuando una familia escribe "al Club" no escribe a nadie en concreto: el
 * mensaje va con `receiver_id` = el id del **admin**, que es el buzón del club
 * y no una persona (mig 204).
 *
 * Las pantallas de Comunicaciones filtraban por `profile.id`, así que un
 * director pedía SUS conversaciones y no tenía ninguna. Medido con su sesión
 * en producción: RLS le dejaba leer 32 mensajes, 4 de ellos del club, y la
 * consulta le devolvía 0.
 *
 * No es un problema de permisos —`is_admin()` es `role IN ('admin','director')`
 * y la policy de SELECT ya se lo permitía todo—: era la consulta. El patrón que
 * dejaba fuera al director no es el rol, es **filtrar por el propio id cuando
 * el dato es del club**.
 *
 * El buzón incluye a los directores
 * ---------------------------------
 * Antes era "los admin activos + mi propio id", y por eso un director no veía
 * lo que había respondido otro: dos personas contestaban lo mismo. El canal del
 * club es uno solo y compartido; cada respuesta lleva el nombre de quien la
 * escribió, que es lo que pinta el hilo.
 *
 * De paso recoge lo que siga contestando la app publicada: hasta que salga el
 * build nuevo, una familia con el hilo partido responde al `sender_id` del
 * director. Con el buzón ampliado esas respuestas las ven todos; sin ampliar se
 * quedaban visibles para uno solo.
 *
 * ⚠️ LA CONDICIÓN QUE VA PEGADA A ESTO
 * ------------------------------------
 * Lo que hace compartido a un mensaje es **quién lo escribe**, no **desde
 * dónde**. Hoy las dos cosas coinciden porque un director solo tiene un sitio
 * desde el que escribir a una familia: el buzón. Medido — las pantallas de
 * monitor no las alcanza (el panel rebota `/monitor/*` a `/dashboard`, y la app
 * monta `MonitorChatScreen` solo en `MonitorNavigator`), y ninguna función SQL
 * inserta en `messages`.
 *
 * El día que un director pueda escribir a una familia FUERA del buzón, su
 * conversación privada se volvería visible para todos los demás sin que nadie
 * lo note: no daría error ni rompería un test, simplemente aparecería. Quien
 * construya esa pantalla añade en el MISMO PR una marca en el mensaje que diga
 * si se escribió desde el club, y este filtro pasa a mirarla. No se añade ahora
 * porque hoy valdría `true` siempre, y un campo que nunca varía no lo mantiene
 * nadie. Ver runbook §21.
 */
export async function idsDelBuzonDelClub(
  miId: string
): Promise<{ ids: string[]; error: string | null }> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id')
    .in('role', ROLES_DEL_BUZON as unknown as string[])
    .eq('active', true);

  // ⚠️ Con el error descartado, el conjunto se quedaba en `[miId]`: el buzón
  // COMPARTIDO se encogía hasta ser el mío, y las conversaciones abiertas con
  // otro del club desaparecían de la lista. No se veía roto, se veía con menos
  // hilos — que es cómo el BUG 13 estuvo dos semanas sin que nadie lo notara.
  if (error) return { ids: [miId], error: error.message || 'Error desconocido' };

  const ids = new Set<string>([miId]);
  for (const fila of (data ?? []) as { id: string }[]) ids.add(fila.id);
  return { ids: [...ids], error: null };
}

/**
 * Cuántos mensajes sin leer tiene EL BUZÓN, no la persona.
 *
 * El globo preguntaba `get_unread_message_count(profile.id)`, que cuenta lo
 * dirigido a uno mismo. Las familias escriben al admin, así que el globo de un
 * director era siempre 0: el buzón compartido lo veía, pero nadie le avisaba.
 * Cuarto sitio del mismo patrón —preguntar por el propio id cuando el dato es
 * del club— después de la consulta, el render y el realtime.
 *
 * No hace falta tocar la función: se cuenta directo sobre `messages`, que es lo
 * que permite preguntar por varios destinatarios a la vez. La policy de SELECT
 * es ALL con `is_admin()`, así que el recuento sale ya filtrado por RLS.
 *
 * El aviso, en cambio, ya estaba compartido desde la mig 146:
 * `notify_admins_new_message` dispara cuando el destinatario es admin O
 * director. Lo que faltaba era el número.
 *
 * ⚠️ SIN LEER POR MÍ, NO «SIN LEER» (mig 324)
 * -------------------------------------------
 * El buzón lo comparten siete personas, y `messages.read` era un booleano por
 * mensaje: el primero que abría un hilo lo apagaba para los otros seis. Medido
 * en producción antes de la 324: 21 de 21 mensajes del buzón marcados como
 * leídos. Ahora se cuenta lo que NO TENGO REGISTRADO YO, con `misLecturas`.
 *
 * Y se cuenta a mano, sin tocar la base: la pregunta es una ausencia de fila, y
 * PostgREST no la sabe decir sin una vista o una RPC. Mismo criterio que trajo
 * esta función aquí en su día.
 */
export async function noLeidosDelBuzon(
  miId: string
): Promise<{ noLeidos: number | null; error: string | null }> {
  // `null` = no se sabe, y no es lo mismo que 0. Esta función nació porque el
  // globo de un director era SIEMPRE 0; devolver 0 cuando la lectura falla
  // reintroduce ese mismo fallo, con la misma cara.
  const { ids, error: errorBuzon } = await idsDelBuzonDelClub(miId);
  if (errorBuzon) return { noLeidos: null, error: errorBuzon };

  // Las personas que pueden escribir al club. Si esto no se puede leer, el
  // recuento es `null` y no 0: sin la lista, el predicado de abajo diría que
  // NADA es entrante y el globo se apagaría afirmando un «todo visto».
  const { ids: deFamilia, error: errorFamilia } = await idsDeFamilia();
  if (errorFamilia || deFamilia === null) {
    return { noLeidos: null, error: errorFamilia ?? 'No se ha podido leer quién escribe al club' };
  }

  const { data, error } = await supabase
    .from('messages')
    .select('id, sender_id, receiver_id, is_broadcast')
    .in('receiver_id', ids)
    .eq('is_broadcast', false)
    .limit(TOPE_IDS);
  if (error) return { noLeidos: null, error: error.message || 'Error desconocido' };
  // El filtro final va AQUÍ y no en la consulta, con `esCorreoEntranteDelClub`:
  // es la misma función que usa la lista para su globo de cada fila, así que los
  // dos números no pueden volver a decir cosas distintas. Hacerlo con un
  // `.in('sender_id', …)` de 111 uuids sería un segundo predicado escrito en
  // otro idioma —y una URL que crece con cada alta—.
  const buzonSet = new Set(ids);
  const familiaSet = new Set(deFamilia);
  const filas = (data ?? []) as {
    id: string;
    sender_id: string;
    receiver_id: string | null;
    is_broadcast: boolean | null;
  }[];
  // ⚠️ EL TOPE SE MIRA SOBRE LA PÁGINA CRUDA, ANTES DE FILTRAR, y es un detalle
  // que se cuela con una lectura rápida: si la consulta devuelve 1000 filas y el
  // filtro deja 300, mirar las 300 diría «cabe» sobre una página cortada, y el
  // número saldría de menos sin un solo error. La pregunta es si la CONSULTA
  // vino completa, no cuántas sobrevivieron.
  if (filas.length >= TOPE_IDS) {
    return {
      noLeidos: null,
      error: `El buzón pasa de ${TOPE_IDS} mensajes: el recuento por persona ya no cabe en una consulta.`,
    };
  }
  const delBuzon = filas
    .filter((m) => esCorreoEntranteDelClub(m, buzonSet, familiaSet))
    .map((m) => m.id);
  const { leidos, error: errorLecturas } = await misLecturas(delBuzon, miId);
  if (errorLecturas) return { noLeidos: null, error: errorLecturas };
  return { noLeidos: delBuzon.filter((id) => !leidos.has(id)).length, error: null };
}

/**
 * Cuántos mensajes sin leer tiene UNA PERSONA, o `null` si no se ha podido
 * contar. Gemelo de `app/src/services/buzon-club.ts`.
 *
 * Los tres sitios que pintan el globo del chat —los tabs del alumno y del
 * entrenador en la app, esta barra lateral— hacían la misma llamada y
 * descartaban el `error`:
 *
 *   const { data } = await supabase.rpc('get_unread_message_count', …);
 *   setUnreadCount(data ?? 0);
 *
 * Y un 0 apaga el globo. Un globo que no está no se lee como «no se ha podido
 * contar», se lee como «no tienes mensajes»: nadie entra a comprobarlo, que es
 * exactamente cómo el BUG 13 —el chat vacío— estuvo dos semanas en producción.
 */
export async function noLeidosDeMensajes(userId: string): Promise<number | null> {
  const { data, error } = await supabase.rpc('get_unread_message_count', {
    p_user_id: userId,
  });
  if (error) return null;
  return data ?? 0;
}

/** El formato que PostgREST espera dentro de un `in.(...)`. */
export function listaIn(ids: string[]): string {
  return `(${ids.join(',')})`;
}
